import React from 'react';
import { difficultyLabel, lessonStatusLabel } from '../../parentPortal/components/Pill';
import { formatDateFull, formatMonthYear } from '../utils/format';

// Formal, print-only rendering of the same portfolio data PortfolioView
// shows on screen. Stays hidden (see src/styles/print.css) until the
// browser's print stylesheet applies — window.print() has no server-side
// PDF to call out to (see docs/portfolio-api-contract.md), so this is the
// actual "export": a plain black-on-white, table-based layout that reads
// like a real transcript instead of printing the app's dark-mode UI chrome
// verbatim (topbar, tabs, an Export PDF button, etc).
export default function TranscriptPrintView({ portfolio, studentLabel }) {
  if (!portfolio) return null;

  const years = portfolio.academicYears || [];
  const agg = portfolio.aggregate || {};
  const first = formatMonthYear(agg.firstActivityAt);
  const last = formatMonthYear(agg.lastActivityAt);
  const coverage = first && last
    ? `${first} – ${last}`
    : first
    ? `Since ${first}`
    : last
    ? `Through ${last}`
    : '—';
  const printedOn = new Date().toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className="transcript-print">
      <header className="transcript-print__letterhead">
        <div className="transcript-print__brand">Graspd.ai</div>
        <div className="transcript-print__doctitle">Academy Learning Record</div>
      </header>

      <div className="transcript-print__meta">
        <div>
          <div className="transcript-print__label">Student</div>
          <div className="transcript-print__value">
            {portfolio.displayName || studentLabel || 'Learner'}
          </div>
        </div>
        <div>
          <div className="transcript-print__label">Coverage Period</div>
          <div className="transcript-print__value">{coverage}</div>
        </div>
        <div>
          <div className="transcript-print__label">Date Printed</div>
          <div className="transcript-print__value">{printedOn}</div>
        </div>
      </div>

      <div className="transcript-print__summary">
        <strong>{agg.totalCoursesCompleted ?? 0}</strong> courses completed
        <span className="transcript-print__dot">•</span>
        <strong>{agg.totalCoursesInProgress ?? 0}</strong> in progress
      </div>

      {years.length === 0 ? (
        <p className="transcript-print__empty">No coursework recorded yet.</p>
      ) : (
        years.map((y) => (
          <section key={y.year} className="transcript-print__year">
            <h2>
              {y.label}
              {y.isCurrent ? ' (in progress)' : ''}
            </h2>
            {(y.subjects || []).map((s) => (
              <div key={s.subject} className="transcript-print__subject">
                <h3>{s.subject}</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Level</th>
                      <th>Lessons</th>
                      <th>Status</th>
                      <th>Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.courses.map((c) => (
                      <tr key={c.courseId}>
                        <td>{c.title}</td>
                        <td>{difficultyLabel(c.difficulty)}</td>
                        <td>
                          {c.completedLessonCount}/{c.lessonCount}
                        </td>
                        <td>{lessonStatusLabel(c.status)}</td>
                        <td>{c.status === 'COMPLETED' ? formatDateFull(c.completedAt) || '—' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </section>
        ))
      )}

      <footer className="transcript-print__footer">
        This record reflects self-directed coursework completed on the Graspd.ai learning platform.
      </footer>
    </div>
  );
}
